import { useDeleteProject } from '@common/projectHooks/useDeleteProject';
import { deleteEntityQuery, QueryKeys } from '@common/queries/common';
import { useVerticalAppsRecentActivitySync } from '@common/VerticalApps/recentActivity/useVerticalAppsRecentActivity';
import { useVerticalAppsSideNavProjectsPreference } from '@common/VerticalApps/useVerticalAppsSideNavProjectsPreference';
import { EntityKind } from '@prophecy/graphql-types/enums';
import { Dialog, toast } from '@prophecy/ui-v3';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useQueryClient } from '@tanstack/react-query';

import { useGraphQlMutation } from '../../../data/util';

/** Confirm, delete, then drop the project from the sidebar and recent-activity preferences. */
export function useDeleteVAppProject() {
  const queryClient = useQueryClient();
  const deleteProject = useDeleteProject(useGraphQlMutation(deleteEntityQuery));
  const { removeSideNavProject } = useVerticalAppsSideNavProjectsPreference();
  const { removeRecentActivityForProject } = useVerticalAppsRecentActivitySync();

  return usePersistentCallback(async (projectId: string, projectName: string) => {
    const confirmation = await Dialog.alert({
      'data-test-id': 'vapp-delete-project-dialog',
      title: 'Delete project',
      subTitle: <span>Are you sure you want to delete &ldquo;{projectName}&rdquo;? This action cannot be undone.</span>,
      okButton: { children: 'Delete', variant: 'primary', danger: true }
    });
    if (confirmation !== Dialog.ResponseAction.okButton) return;

    const loadingToast = toast.loading({
      content: `Deleting ${projectName}`,
      'data-test-id': 'vapp-delete-project-toast'
    });
    try {
      await deleteProject.mutateAsync({ uid: projectId, entityKind: EntityKind.Project });
      loadingToast.update({ content: 'Project deleted', variant: 'success' });
      queryClient.invalidateQueries([QueryKeys.ProjectList]);
      removeSideNavProject(projectId);
      removeRecentActivityForProject(projectId);
    } catch {
      loadingToast.update({ content: 'Failed to delete project', variant: 'error' });
    }
  });
}
